import React from 'react';
import { FormattedMessage } from 'react-intl';
import Dialog from '@rio-cloud/rio-uikit/lib/es/Dialog';
import CampaignDetailsForm from '~/features/campaigns/components/campaignForm/CampaignDetailsForm';
import CampaignTargetDeliverableForm from '~/features/campaigns/components/campaignForm/CampaignTargetDeliverableForm';
import CampaignTargetVehiclesForm from '~/features/campaigns/components/campaignForm/CampaignTargetVehiclesForm';
import { Campaign } from '~/api/models/campaign';
import { Control } from 'react-hook-form';
import { useEditCampaignMutation } from '~/api/campaigns/Campaign.api';
import { toSemanticVersion } from '~/features/base/utils/versionNumberConverter';
import Notification from '@rio-cloud/rio-uikit/Notification';

const SECTION_TYPE = {
    DETAILS: 'details',
    DELIVERABLE: 'deliverable',
    VEHICLES: 'vehicles'
} as const;

type SectionType = typeof SECTION_TYPE[keyof typeof SECTION_TYPE];

interface EditCampaignSectionDialogProps {
    show: boolean;
    onHide: () => void;
    campaign: Campaign;
    control: Control;
    setValue: any;
    formValues: any;
    sectionType: SectionType;
    reset: any;
    onSave: () => void;
}

const EditCampaignSectionDialog: React.FC<EditCampaignSectionDialogProps> = ({
    show,
    onHide,
    campaign,
    control,
    setValue,
    formValues,
    sectionType,
    reset,
    onSave
}) => {
    const [editCampaign] = useEditCampaignMutation();

    const DIALOG_CONFIG = {
        [SECTION_TYPE.DETAILS]: {
            title: <FormattedMessage id='intl-msg:campaign:editCampaignDetails'/>,
            form: <CampaignDetailsForm control={control}/>
        },
        [SECTION_TYPE.DELIVERABLE]: {
            title: <FormattedMessage id='intl-msg:campaign:editTargetDeliverable'/>,
            form: <CampaignTargetDeliverableForm control={control} setValue={setValue} formValues={formValues}/>
        },
        [SECTION_TYPE.VEHICLES]: {
            title: <FormattedMessage id='intl-msg:campaign:editTargetVehicles'/>,
            form: <CampaignTargetVehiclesForm control={control} setValue={setValue}/>
        }
    };

    const getDialogTitle = () => DIALOG_CONFIG[sectionType].title;
    const getDialogForm = () => DIALOG_CONFIG[sectionType].form;

    const handleSave = () => {
        // Define field mappings between form and API
        const fieldMappings = {
            [SECTION_TYPE.DETAILS]: [
                { form: 'deviceType', api: 'deviceType' },
                { form: 'decisionBoardUrl', api: 'decisionBoard' },
                { form: 'description', api: 'description' }
            ],
            [SECTION_TYPE.DELIVERABLE]: [
                { form: 'deliverableType', api: 'deliverableType' },
                { form: 'deliverableId', api: 'deliverableId' },
                { form: 'deliverableVersion', api: 'deliverableVersion', transform: true }
            ],
            [SECTION_TYPE.VEHICLES]: [
                { form: 'targetGroups', api: 'vehicleGroup' }
            ]
        };

        const payload = { id: campaign.id, };

        // Process only the fields for the current section
        fieldMappings[sectionType].forEach(mapping => {
            const formValue = formValues[mapping.form];

            payload[mapping.api] = mapping.transform ? toSemanticVersion(formValue) : formValue;

        });

        editCampaign(payload)
            .unwrap()
            .then(() => {
                onSave();
                onHide();
            })
            .catch((error) => {
                console.error('Failed to edit campaign:', error);
                Notification.error(error);
            });
    };

    const handleCancel = () => {
        onHide();
    };

    return (
        <Dialog
            show={show}
            onHide={handleCancel}
            title={<h4>{getDialogTitle()}</h4>}
            body={getDialogForm()}
            footer={
                <div className='display-flex justify-content-end gap-10'>
                    <button
                        className='btn btn-default'
                        onClick={handleCancel}
                    >
                        <FormattedMessage id='intl-msg:cancel'/>
                    </button>
                    <button
                        className='btn btn-primary'
                        onClick={handleSave}
                    >
                        <FormattedMessage id='intl-msg:save'/>
                    </button>
                </div>
            }
        />
    );
};

export default EditCampaignSectionDialog;
